import {
    Dialog,
    DialogBackdrop,
    DialogPanel,
    DialogTitle,
    Button, Select, Input,
} from "@headlessui/react";
import React, {useState} from "react";
import { ReactComponent as PFC } from "../../customIcons/PFC.svg";
import {useDarkMode} from "../../contexts/darkModeContext";
import {useAuth} from "../../contexts/authContext";
import {
    ExclamationTriangleIcon, RocketLaunchIcon, XCircleIcon,
} from "@heroicons/react/24/outline";
import CustomButton from "../Widgets/CustomButton";
import {ChevronLeftIcon, ChevronRightIcon} from "@heroicons/react/20/solid";
import EmptySVG from "../Widgets/EmptySVG";
import {useNotification} from "../Notifications/NotificationProvider";
import PerformanceCard from "./PerformanceCard";
import {calculateTimeDifference} from "../Performance";
import PerformanceChart from "./PerformanceChart";

export const countOrdersInTimeRange = (orders, minMinutes = 0, maxMinutes = Infinity) => {
  if (!orders || !Array.isArray(orders)) return 0;

  return orders.reduce((count, order) => {
    if (!order?.submittedTime || !order?.csrTime) return count;

    let submitted = order.submittedTime;
    let csrTime = order.csrTime;

    // Normalize csrTime (same logic you used before)
    if (typeof csrTime === "object" && csrTime.seconds) {
      csrTime = csrTime.seconds * 1000;
    }

    if (typeof csrTime === "string") {
      csrTime = new Date(csrTime).getTime();
    }

    if (typeof submitted === "object" && submitted.seconds) {
      submitted = submitted.seconds * 1000;
    }

    if (typeof submitted === "string") {
      submitted = new Date(submitted).getTime();
    }

    if (typeof csrTime !== "number" || typeof submitted !== "number") return count;

    const minutes = calculateTimeDifference(submitted, csrTime, true);

    if (minutes >= minMinutes && minutes <= maxMinutes) {
      return count + 1;
    }

    return count;
  }, 0);
};

export const getAverageResponseTime = (orders) => {
  if (!orders || orders.length === 0) return 0;

  let total = 0;
  let count = 0;

  orders.forEach((order) => {
    if (!order?.submittedTime || !order?.csrTime) return;

    let submitted = order.submittedTime;
    let csrTime = order.csrTime;

    // Normalize csrTime
    if (typeof csrTime === "object" && csrTime.seconds) {
      csrTime = csrTime.seconds * 1000;
    }
    if (typeof csrTime === "string") {
      csrTime = new Date(csrTime).getTime();
    }

    // Normalize submitted
    if (typeof submitted === "object" && submitted.seconds) {
      submitted = submitted.seconds * 1000;
    }
    if (typeof submitted === "string") {
      submitted = new Date(submitted).getTime();
    }

    if (typeof csrTime !== "number" || typeof submitted !== "number") return;

    const minutes = calculateTimeDifference(submitted, csrTime, true);

    total += minutes;
    count++;
  });

  return count === 0 ? 0 : Math.round(total / count);
};

const WeeklyBreakdown = ({ open, onClose, CSR, orders }) => {
  
    const { darkMode } = useDarkMode();
    const { currentUser } = useAuth();
    const { triggerNotification } = useNotification();
    
    const [itemsPerPage, setItemsPerPage] = useState(15);
    const [currentPage, setCurrentPage] = useState(1);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [hasFiltered, setHasFiltered] = useState(false);

    const totalPages = Math.ceil((filteredOrders.length || 1) / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredOrders.slice(startIndex, startIndex + itemsPerPage);
    
    const handleFilter = () => {
      if (!startDate || !endDate) return;

      // Normalize start to beginning of day and end to end of day
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      const startTime = start.getTime();
      const endTime = end.getTime();

      const filtered = orders.filter((order) => {
        if (!order.csrTime) return false;

        let time = order.csrTime;

        // Handle Firestore Timestamp object
        if (typeof time === "object" && time.seconds) {
          time = time.seconds * 1000;
        }

        // Handle string dates
        if (typeof time === "string") {
          time = new Date(time).getTime();
        }

        // At this point it should be a number (ms)
        if (typeof time !== "number") return false;

        return time >= startTime && time <= endTime;
      });

      setFilteredOrders(filtered);
      setHasFiltered(true);
      setCurrentPage(1);
    };

    const fastCount = hasFiltered ? countOrdersInTimeRange(filteredOrders, 0, 45) : 0;
    const moderateCount = hasFiltered ? countOrdersInTimeRange(filteredOrders, 46, 90) : 0;
    const slowCount = hasFiltered ? countOrdersInTimeRange(filteredOrders, 91, Infinity) : 0;
    
    return (
      <Dialog open={open} onClose={onClose} className="relative z-50 font-numbers">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/50 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
    
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-6">
            <DialogPanel
              transition
              className={`w-full max-w-7xl h-[calc(100dvh-10rem)] rounded-2xl shadow-xl border
              ${darkMode ? 'bg-darkMainColor border-neutral-700 text-white' : 'bg-white border-gray-200 text-gray-900'} transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`}
            >
                {/* Header */}
                <div className={`flex flex-col items-start gap-3 px-6 py-4`}>
                    
                    <div className="flex w-full items-center justify-between">
                        
                        <div className={`flex flex-col`}>
                            <DialogTitle className={`flex items-center italic gap-x-1`}>
                                <div className={`flex items-center text-sm gap-x-1`}>
                                    <div className={`text-lg`}>Performance History</div>
                                </div>
                            </DialogTitle>
                            <div className={`text-xs text-gray-500 -mt-0.5`}>Based on the selected date range for the
                                intended rep
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className={`border rounded-md px-2 py-1 text-sm ${darkMode ? 'bg-darkMainColor border-neutral-700' : ''}`}
                            />
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className={`border rounded-md px-2 py-1 text-sm ${darkMode ? 'bg-darkMainColor border-neutral-700' : ''}`}
                            />
                            <Button
                                onClick={handleFilter}
                                className="bg-green-600 text-white px-3 py-1 rounded-md text-sm"
                            >
                                Apply
                            </Button>
                        </div>
                    
                    </div>
                
                
                </div>
                
                {/*Service Body*/}
                <div className={`flex flex-col h-[calc(100dvh-19rem)] ${darkMode ? 'border-neutral-700' : ''}`}>
                    
                    {/*Header*/}
                    <div className={`bg-gray-700/10 border-y border-opacity-50 ${darkMode ? 'border-neutral-700' : ''}`}>
                        {(() => {
                            const stats = [
                                {
                                    label: "Average Time",
                                    value: hasFiltered ? getAverageResponseTime(filteredOrders) : 0,
                                    previous: "Time for order completion",
                                    unit: "mins",
                                    icon: PFC,
                                    positive: true,
                                },
                                {
                                    label: "Fast Responses",
                                    value: fastCount,
                                    previous: "Response time under 45 minutes",
                                    icon: RocketLaunchIcon,
                                    positive: true,
                                    color: `text-green-600`
                                  },
                                  {
                                    label: "Moderate Responses",
                                    value: moderateCount,
                                    previous: "Response time between 46–90 minutes",
                                    icon: ExclamationTriangleIcon,
                                    positive: false,
                                    color: `text-yellow-600`
                                  },
                                  {
                                    label: "Slow Responses",
                                    value: slowCount,
                                    previous: "Response time greater than 91 minutes",
                                    icon: XCircleIcon,
                                    positive: false,
                                    color: `text-red-600`
                                  },
                                ];

                        return (
                          <div className={`grid grid-cols-4 overflow-hidden divide-x ${darkMode ? 'divide-neutral-700' : ''}`}>
                            {stats.map((stat, index) => {

                              return (
                                <a
                                  key={index}
                                  href="#"
                                  className={`block p-2 relative`}
                                >
                                  <div className="flex flex-col lg:flex-row gap-y-1 gap-x-3">
                                    <stat.icon className={`shrink-0 size-5 text-muted-foreground ${stat.color || "text-green-600"}`} />

                                    <div className="grow">

                                        {/*Title*/}
                                      <p className="text-xs uppercase font-medium text-gray-500">
                                        {stat.label}
                                      </p>

                                        {/*Value*/}
                                      <h3 className={`flex items-end gap-x-2 text-xl -mt-1 sm:text-2xl font-semibold ${stat.color || ""}`}>
                                        <span className={``}>{stat.value}</span>
                                        <span className={`text-gray-500 text-sm mb-1 ${stat.unit ? '' : 'hidden'}`}>{stat.unit}</span>
                                      </h3>

                                        {/*Previous*/}
                                      <div className="flex -mt-1 justify-between items-center text-gray-500">
                                        <p className="text-xs text-muted-foreground-1">
                                          <span className="font-semibold text-foreground">
                                            {stat.previous}
                                          </span>
                                        </p>

                                        <span
                                          className={` ${stat.change ? '' : 'hidden'} ms-1 inline-flex items-center gap-1.5 py-1 px-2 rounded-md text-xs font-medium ${
                                            stat.positive
                                              ? "bg-green-100 text-green-700"
                                              : "bg-red-100 text-red-700"
                                          }`}
                                        >
                                          {stat.change}
                                        </span>
                                      </div>

                                    </div>
                                  </div>
                                </a>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/*Orders Section*/}
                    <div className={`h-[calc(100dvh-25rem)] scrollbar-hide grid grid-cols-3`}>
                        
                        {/*Table*/}
                        <div className={`col-span-2 relative h-[calc(100dvh-26.5rem)] overflow-y-scroll scrollbar-none`}>
                            {!hasFiltered ? (
                                // Display custom dialog if sprays array is empty
                                <div className="absolute flex w-full h-full justify-center items-center">
                                    
                                    <div className={`flex animate-fadeInFast flex-col space-y-2 ${darkMode ? "text-white" : "text-black"} w-fit px-24 py-3 h-fit hover:scale-105`}>
                                        
                                        {/*Icon*/}
                                        <div className={`size-12 border border-opacity-50 ${darkMode ? 'bg-bgcard border-neutral-700' : 'bg-bgcolorLight'} flex items-center justify-center rounded-lg`}>
                                            <PFC className={`size-8 text-gray-400`}/>
                                        </div>
                                        
                                        {/*Title*/}
                                        <div className={`flex flex-col space-y-1`}>
                                            <h3 className="text-xl font-bold">Select a date range to view performance</h3>
                                            <p className="text-xs text-gray-500">Get started by creating a new service log for this machine</p>
                                        </div>
                                        
                                    
                                    </div>
                                    
                                    <EmptySVG/>
                                </div>
                            ) : filteredOrders.length === 0 ? (
                                // Display no results for selected range
                                <div className="absolute flex w-full h-full justify-center items-center">
                                    <div className={`flex animate-fadeInFast flex-col space-y-4 ${darkMode ? "text-white" : "text-black"} w-fit px-24 py-3 h-fit hover:scale-105`}>
                                        <div className={`size-12 border border-opacity-50 ${darkMode ? 'bg-bgcard border-neutral-700' : 'bg-bgcolorLight'} flex items-center justify-center rounded-lg`}>
                                            <PFC className={`size-8 text-gray-400`}/>
                                        </div>
                                        <div className={`flex flex-col space-y-1`}>
                                            <h3 className="text-xl font-bold">No results found for selected range</h3>
                                            <p className="text-xs text-gray-500">Try adjusting your date range and apply again</p>
                                        </div>
                                    </div>
                                    <EmptySVG/>
                                </div>
                            ) : (
                                <>
                                    <div className={`grid grid-cols-1 px-2 auto-rows-auto divide-y divide-opacity-50 ${darkMode ? 'divide-neutral-700' : ''} divide-dashed`}>
                                        {currentItems.map((order, index) => (
                                            <PerformanceCard
                                                index={index}
                                                CSR={CSR}
                                                order={order}
                                                short={true}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        
                        {/*Chart*/}
                        <div className={`h-full p-3 flex items-center justify-center col-span-1 ${darkMode ? 'bg-white/5' : 'bg-black/5'}`}>
                        
                            <PerformanceChart
                              title="CSR Response Speed"
                              fast={fastCount}
                              moderate={moderateCount}
                              slow={slowCount}
                            />
                        
                        </div>
                        
                    </div>
                    
                    {/*Bottom*/}
                    <div className={`border-y border-opacity-50 ${darkMode ? 'border-neutral-700' : ''}`}>
                        <nav
                            aria-label="Pagination"
                            className={`flex items-center justify-between px-4 py-1`}
                        >
                            <div className="flex items-center space-x-3">
                                <p className="text-sm">
                                    Showing <span className="font-medium">{startIndex + 1}</span> to <span
                                    className="font-medium">{Math.min(startIndex + itemsPerPage, filteredOrders.length)}</span> of{' '}
                                    <span className="font-medium">{filteredOrders.length}</span> results
                                </p>
                                <Select
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className={`border rounded-lg p-1 ${darkMode ? "bg-darkMainColor border-neutral-700" : ""}`}
                                >
                                    <option value={15}>15</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={75}>75</option>
                                </Select>
                            </div>
                            <div className="flex flex-1 justify-between sm:justify-end gap-x-3">
                                <CustomButton
                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                    color="green"
                                    disabled={currentPage === 1}
                                    icon={ChevronLeftIcon}
                                    text="Previous"
                                />
                                
                                <CustomButton
                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                    color="green"
                                    disabled={currentPage === totalPages}
                                    icon={ChevronRightIcon}
                                    text="Next"
                                    flip={true}
                                />
                            </div>
                        </nav>
                    </div>
                
                </div>
                
                {/* Footer */}
                <div className={`flex justify-end gap-2 px-6 py-4`}>
                    <Button
                        onClick={onClose}
                        className={`rounded-md px-4 py-2 text-sm font-medium
                          ${darkMode ? 'bg-gray-500/20' : 'bg-gray-200'}
                          hover:opacity-80`}
                    >
                        Cancel
                    </Button>
                    
                    {/*<Button*/}
                    {/*    // onClick={submit}*/}
                    {/*    className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-500 disabled:opacity-40"*/}
                    {/*>*/}
                    {/*    Download Service Report*/}
                    {/*</Button>*/}
                </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    );
}

export default WeeklyBreakdown;
